import {
  Badge,
  Button,
  EmptyState,
  IconButton,
  Table,
  type TableSortDirection,
} from '@ntconsult/aluma-react';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { PoolColumnsState } from '../hooks/usePoolColumns';
import type { PoolFilterState } from '../hooks/usePoolFilters';
import type { PoolAllocation, PoolFilterOptions, PoolSortField } from '../types/Checks';
import { formatDisplayDate } from '../utils/data';
import { formatMoney } from '../utils/money';
import { poolTenureTone } from '../utils/poolTenure';
import type { EditAllocationTarget } from './AllocationModal';
import { TagBadge } from './TagBadge';
import {
  COLUMN_MIN_WIDTH,
  PINNED_BLOCK,
  STICKY_END_CELL_PLAIN,
  STICKY_END_HEADER_PLAIN,
  STICKY_START_CELL_PLAIN,
  STICKY_START_HEADER_PLAIN,
  WIDE_TABLE_CONTAINER,
} from './wideTable';
import { YesNoBadge } from './YesNoBadge';

const PERCENTAGE_STEP = 5;

/** Início da alocação de pool, realçado pelo tempo no pool (T21): laranja > 1 mês, vermelho > 2 meses. */
function PoolStartDate({
  startDate,
  referenceDate,
  locale,
}: {
  startDate: string;
  referenceDate: string | undefined;
  locale: string;
}) {
  const date = formatDisplayDate(startDate, locale);
  const tone = poolTenureTone(startDate, referenceDate);
  if (!tone) return <>{date}</>;
  return (
    <Badge variant="soft" tone={tone} shape="rounded" size="sm">
      {date}
    </Badge>
  );
}

type PoolTableProps = {
  items: PoolAllocation[];
  filterOptions: PoolFilterOptions;
  filterState: PoolFilterState;
  columnsState: PoolColumnsState;
  /** Data de referência da consulta: baliza o realce por tempo no pool (T21). */
  referenceDate: string | undefined;
  onEdit: (target: EditAllocationTarget) => void;
};

/**
 * No molde de `AllocationsTable`; os filtros são os do `PoolList.php` do legado.
 * Ocultar uma coluna não remove o filtro dela: o chip continua acima da tabela.
 */
export function PoolTable({
  items,
  filterOptions,
  filterState,
  columnsState,
  referenceDate,
  onEdit,
}: PoolTableProps) {
  const { t, i18n } = useTranslation(['alocacao', 'common']);
  const { filters, setSort, setSearch, setIds, setPercentageRange, setBillable, setNotified } =
    filterState;
  const { isVisible, visibleColumns } = columnsState;
  // +2: "Profissional" e "Ações", sempre visíveis (fora do seletor e da exportação).
  const columnCount = visibleColumns.length + 2;

  // Totais por moeda: o Axiis nunca converte, então BRL e USD não se somam.
  const costTotals = [
    ...items
      .reduce((totals, { allocatedCost, costCurrency }) => {
        if (allocatedCost == null) return totals;
        const iso = costCurrency ?? '';
        return totals.set(iso, (totals.get(iso) ?? 0) + allocatedCost);
      }, new Map<string, number>())
      .entries(),
  ];
  const costColumnsBefore = visibleColumns.indexOf('allocatedCost');

  const sortProps = (field: PoolSortField) => ({
    sortable: true,
    sortDirection: (filters.sortBy === field
      ? filters.sortDirection
      : 'none') as TableSortDirection,
    onSortChange: (next: TableSortDirection) => setSort(field, next),
  });

  const yesNoOptions = [
    { value: 'true', label: t('common:yes') },
    { value: 'false', label: t('common:no') },
  ];

  return (
    <Table density="compact" stickyHeader containerClassName={WIDE_TABLE_CONTAINER}>
      <Table.Caption className="sr-only">{t('consultas.pool.titulo')}</Table.Caption>
      <Table.Head>
        <Table.Row>
          <Table.Header
            className={`${COLUMN_MIN_WIDTH.medium} ${STICKY_START_HEADER_PLAIN}`}
            {...sortProps('professional')}
            filter={
              <Table.TextFilter
                label={t('alocacao:consultas.colunas.profissional')}
                placeholder={t('alocacao:consultas.filtros.nomeOuMatriculaPlaceholder')}
                value={filters.professionalSearch ?? ''}
                onValueChange={(value) => setSearch('professionalSearch', value)}
              />
            }
          >
            {t('alocacao:consultas.colunas.profissional')}
          </Table.Header>
          {isVisible('profile') && (
            <Table.Header
              className={COLUMN_MIN_WIDTH.short}
              {...sortProps('profile')}
              filter={
                <Table.TextFilter
                  label={t('alocacao:consultas.colunas.perfil')}
                  value={filters.profileSearch ?? ''}
                  onValueChange={(value) => setSearch('profileSearch', value)}
                />
              }
            >
              {t('alocacao:consultas.colunas.perfil')}
            </Table.Header>
          )}
          {isVisible('allocatedPercentage') && (
            <Table.Header
              align="right"
              className="whitespace-nowrap"
              {...sortProps('allocatedPercentage')}
              filter={
                <Table.NumberRangeFilter
                  label={t('alocacao:tabela.percAlocado')}
                  step={PERCENTAGE_STEP}
                  value={{ min: filters.minPercentage ?? null, max: filters.maxPercentage ?? null }}
                  onValueChange={setPercentageRange}
                />
              }
            >
              {t('alocacao:tabela.percAlocado')}
            </Table.Header>
          )}
          {isVisible('billable') && (
            <Table.Header
              align="center"
              className="whitespace-nowrap"
              filter={
                <Table.OptionsFilter
                  label={t('alocacao:tabela.faturando')}
                  options={yesNoOptions}
                  value={filters.billable === undefined ? [] : [String(filters.billable)]}
                  // Sim e Não marcados juntos = sem filtro.
                  onValueChange={(values) =>
                    setBillable(values.length === 1 ? values[0] === 'true' : null)
                  }
                />
              }
            >
              {t('alocacao:tabela.faturando')}
            </Table.Header>
          )}
          {isVisible('contractType') && (
            <Table.Header align="center" className="whitespace-nowrap">
              {t('alocacao:consultas.colunas.tipo')}
            </Table.Header>
          )}
          {isVisible('currency') && (
            <Table.Header align="center" className="whitespace-nowrap">
              {t('alocacao:consultas.colunas.moeda')}
            </Table.Header>
          )}
          {isVisible('allocatedCost') && (
            <Table.Header align="right" className="whitespace-nowrap">
              {t('alocacao:tabela.custoAlocado')}
            </Table.Header>
          )}
          {isVisible('startDate') && (
            <Table.Header align="right" className="whitespace-nowrap" {...sortProps('startDate')}>
              {t('alocacao:consultas.colunas.poolDesde')}
            </Table.Header>
          )}
          {isVisible('notified') && (
            <Table.Header
              align="center"
              className="whitespace-nowrap"
              filter={
                <Table.OptionsFilter
                  label={t('alocacao:consultas.colunas.avisado')}
                  options={yesNoOptions}
                  value={filters.notified === undefined ? [] : [String(filters.notified)]}
                  onValueChange={(values) =>
                    setNotified(values.length === 1 ? values[0] === 'true' : null)
                  }
                />
              }
            >
              {t('alocacao:consultas.colunas.avisado')}
            </Table.Header>
          )}
          {isVisible('noticeDate') && (
            <Table.Header align="right" className="whitespace-nowrap" {...sortProps('noticeDate')}>
              {t('alocacao:consultas.colunas.avisadoEm')}
            </Table.Header>
          )}
          {isVisible('noticeDays') && (
            <Table.Header align="right" className="whitespace-nowrap" {...sortProps('noticeDays')}>
              {t('alocacao:consultas.colunas.numeroDias')}
            </Table.Header>
          )}
          {isVisible('endDate') && (
            <Table.Header align="right" className="whitespace-nowrap" {...sortProps('endDate')}>
              {t('alocacao:consultas.colunas.fimAlocacao')}
            </Table.Header>
          )}
          {isVisible('notes') && (
            <Table.Header
              className={COLUMN_MIN_WIDTH.long}
              filter={
                <Table.TextFilter
                  label={t('alocacao:filtros.anotacoes')}
                  value={filters.notes ?? ''}
                  onValueChange={(value) => setSearch('notes', value)}
                />
              }
            >
              {t('alocacao:filtros.anotacoes')}
            </Table.Header>
          )}
          {isVisible('tags') && (
            <Table.Header
              className={COLUMN_MIN_WIDTH.short}
              filter={
                <Table.OptionsFilter
                  label={t('alocacao:form.tags')}
                  options={filterOptions.tags.map((tag) => ({
                    value: String(tag.id),
                    label: tag.name,
                  }))}
                  value={(filters.tagIds ?? []).map(String)}
                  onValueChange={(values) => setIds('tagIds', values.map(Number))}
                />
              }
            >
              {t('alocacao:form.tags')}
            </Table.Header>
          )}
          {isVisible('responsible') && (
            <Table.Header className={COLUMN_MIN_WIDTH.medium}>
              {t('alocacao:consultas.colunas.responsavel')}
            </Table.Header>
          )}
          {isVisible('lastProject') && (
            <Table.Header className={COLUMN_MIN_WIDTH.long}>
              {t('alocacao:consultas.colunas.ultimaAlocacao')}
            </Table.Header>
          )}
          <Table.Header align="right" className={STICKY_END_HEADER_PLAIN}>
            {t('alocacao:tabela.acoes')}
          </Table.Header>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {items.length === 0 ? (
          // Vazio por filtro mantém o cabeçalho: é nele que o filtro se ajusta.
          <Table.Row>
            <Table.Cell colSpan={columnCount}>
              <div className={PINNED_BLOCK}>
                <EmptyState
                  title={t('alocacao:consultas.pool.filtroSemResultadoTitulo')}
                  description={t('alocacao:consultas.pool.filtroSemResultadoDescricao')}
                  action={
                    <Button variant="secondary" onClick={filterState.clearFilters}>
                      {t('alocacao:filtros.limpar')}
                    </Button>
                  }
                />
              </div>
            </Table.Cell>
          </Table.Row>
        ) : (
          items.map((allocation) => (
            <Table.Row key={allocation.id}>
              <Table.Cell className={STICKY_START_CELL_PLAIN}>
                {allocation.registrationNumber ?? '—'} · {allocation.professionalName}
              </Table.Cell>
              {isVisible('profile') && <Table.Cell>{allocation.profile || '—'}</Table.Cell>}
              {isVisible('allocatedPercentage') && (
                <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
                  {allocation.allocatedPercentage}%
                </Table.Cell>
              )}
              {isVisible('billable') && (
                <Table.Cell align="center">
                  <YesNoBadge value={allocation.billable} />
                </Table.Cell>
              )}
              {isVisible('contractType') && (
                <Table.Cell align="center">{allocation.contractType || '—'}</Table.Cell>
              )}
              {isVisible('currency') && (
                <Table.Cell align="center">{allocation.costCurrency ?? '—'}</Table.Cell>
              )}
              {isVisible('allocatedCost') && (
                <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
                  {formatMoney(
                    allocation.allocatedCost,
                    allocation.costCurrency ?? undefined,
                    i18n.language,
                  )}
                </Table.Cell>
              )}
              {isVisible('startDate') && (
                <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
                  <PoolStartDate
                    startDate={allocation.startDate}
                    referenceDate={referenceDate}
                    locale={i18n.language}
                  />
                </Table.Cell>
              )}
              {isVisible('notified') && (
                <Table.Cell align="center">
                  <YesNoBadge value={allocation.notified} />
                </Table.Cell>
              )}
              {isVisible('noticeDate') && (
                <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
                  {allocation.noticeDate
                    ? formatDisplayDate(allocation.noticeDate, i18n.language)
                    : '—'}
                </Table.Cell>
              )}
              {isVisible('noticeDays') && (
                <Table.Cell align="right" className="tabular-nums">
                  {allocation.notified ? allocation.noticeDays : '—'}
                </Table.Cell>
              )}
              {isVisible('endDate') && (
                <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
                  {allocation.endDate
                    ? formatDisplayDate(allocation.endDate, i18n.language)
                    : t('alocacao:tabela.semTermino')}
                </Table.Cell>
              )}
              {isVisible('notes') && (
                <Table.Cell className="max-w-xs whitespace-pre-line">
                  {allocation.notes || '—'}
                </Table.Cell>
              )}
              {isVisible('tags') && (
                <Table.Cell>
                  <div className="flex flex-wrap gap-1">
                    {allocation.tags.map((tag) => (
                      <TagBadge key={tag.id} tag={tag} />
                    ))}
                  </div>
                </Table.Cell>
              )}
              {isVisible('responsible') && <Table.Cell>{allocation.responsible || '—'}</Table.Cell>}
              {isVisible('lastProject') && (
                <Table.Cell>
                  {allocation.lastProject
                    ? `${allocation.lastProject.axisCode} - ${allocation.lastProject.name}`
                    : '—'}
                </Table.Cell>
              )}
              <Table.Cell align="right" className={STICKY_END_CELL_PLAIN}>
                {allocation.allocation && (
                  <IconButton
                    icon={<Pencil size={14} />}
                    label={t('alocacao:consultas.pool.editarAlocacaoDe', {
                      nome: allocation.professionalName,
                    })}
                    variant="icon"
                    size="sm"
                    onClick={() =>
                      allocation.allocation &&
                      onEdit({
                        allocation: allocation.allocation,
                        professional: {
                          id: allocation.professionalId,
                          registrationNumber: allocation.registrationNumber,
                          name: allocation.professionalName,
                        },
                      })
                    }
                  />
                )}
              </Table.Cell>
            </Table.Row>
          ))
        )}
      </Table.Body>
      {items.length > 0 && isVisible('allocatedCost') && (
        <Table.Footer className="border-t-0 bg-surface">
          <Table.Row className="border-0 font-bold">
            <Table.Cell colSpan={costColumnsBefore + 1} className={STICKY_START_CELL_PLAIN} />
            <Table.Cell
              align="right"
              className="whitespace-nowrap tabular-nums"
              aria-label={t('alocacao:consultas.pool.totalCustoAlocado')}
            >
              {costTotals.map(([iso, total]) => formatMoney(total, iso, i18n.language)).join(' · ')}
            </Table.Cell>
            <Table.Cell colSpan={columnCount - costColumnsBefore - 2} />
          </Table.Row>
        </Table.Footer>
      )}
    </Table>
  );
}
